import { PlanStep, StepStatus } from '../types/generated/graphql';

/**
 * Represents a node in the task tree with its children
 */
export interface TaskTreeNode {
  step: PlanStep;
  children: TaskTreeNode[];
}

/**
 * Aggregated progress and status metrics for a task and its descendants
 */
export interface TaskAggregates {
  total: number;
  done: number;
  inProgress: number;
  blocked: number;
  nextUp: PlanStep | null;
  needsAttention: boolean;
}

/**
 * The complete task tree structure with lookup maps
 */
export interface TaskTree {
  rootTasks: TaskTreeNode[];
  childrenByParent: Map<string, PlanStep[]>;
  aggregatesById: Map<string, TaskAggregates>;
}

/**
 * Plan-level progress metrics
 */
export interface PlanProgress {
  total: number;
  done: number;
  inProgress: number;
  blocked: number;
  percentComplete: number;
}

/**
 * Check if a step's dependencies are all satisfied (completed)
 */
export function areDependenciesSatisfied(
  step: PlanStep,
  stepsById: Map<string, PlanStep>
): boolean {
  if (!step.dependsOn || step.dependsOn.length === 0) {
    return true;
  }
  return step.dependsOn.every((depId) => {
    const depStep = stepsById.get(depId);
    return depStep?.status === StepStatus.Completed;
  });
}

/**
 * Find the first actionable step in a subtree (not started, dependencies satisfied)
 */
export function findNextUp(
  node: TaskTreeNode,
  stepsById: Map<string, PlanStep>
): PlanStep | null {
  // Check the node itself first
  if (
    node.step.status === StepStatus.NotStarted &&
    areDependenciesSatisfied(node.step, stepsById)
  ) {
    return node.step;
  }

  // Check children recursively
  for (const child of node.children) {
    const nextUp = findNextUp(child, stepsById);
    if (nextUp) {
      return nextUp;
    }
  }

  return null;
}

/**
 * Check if any descendant needs attention (blocked or has unmet dependencies)
 */
function checkNeedsAttention(
  node: TaskTreeNode,
  stepsById: Map<string, PlanStep>
): boolean {
  // Check if this step is blocked
  if (node.step.status === StepStatus.Blocked) {
    return true;
  }

  // Check if this step has unmet dependencies (not completed and deps not satisfied)
  if (
    node.step.status !== StepStatus.Completed &&
    !areDependenciesSatisfied(node.step, stepsById)
  ) {
    return true;
  }

  // Check children recursively
  for (const child of node.children) {
    if (checkNeedsAttention(child, stepsById)) {
      return true;
    }
  }

  return false;
}

/**
 * Compute aggregated metrics for a task tree node
 */
export function computeAggregates(
  node: TaskTreeNode,
  stepsById: Map<string, PlanStep>
): TaskAggregates {
  let total = 1; // Include the node itself
  let done = node.step.status === StepStatus.Completed ? 1 : 0;
  let inProgress = node.step.status === StepStatus.InProgress ? 1 : 0;
  let blocked = node.step.status === StepStatus.Blocked ? 1 : 0;

  // Aggregate from children
  for (const child of node.children) {
    const childAggregates = computeAggregates(child, stepsById);
    total += childAggregates.total;
    done += childAggregates.done;
    inProgress += childAggregates.inProgress;
    blocked += childAggregates.blocked;
  }

  return {
    total,
    done,
    inProgress,
    blocked,
    nextUp: findNextUp(node, stepsById),
    needsAttention: checkNeedsAttention(node, stepsById),
  };
}

/**
 * Build a tree node recursively
 */
function buildTreeNode(
  step: PlanStep,
  childrenByParent: Map<string, PlanStep[]>
): TaskTreeNode {
  const children = childrenByParent.get(step.id) || [];
  return {
    step,
    children: children.map((child) => buildTreeNode(child, childrenByParent)),
  };
}

/**
 * Build the complete task tree from a flat list of steps
 */
export function buildTaskTree(steps: PlanStep[]): TaskTree {
  // Create lookup maps
  const stepsById = new Map(steps.map((s) => [s.id, s]));
  const childrenByParent = new Map<string, PlanStep[]>();

  // Group children by parent
  for (const step of steps) {
    const parentId = step.parentStepId;
    if (parentId) {
      const siblings = childrenByParent.get(parentId) || [];
      siblings.push(step);
      childrenByParent.set(parentId, siblings);
    }
  }

  // Find root tasks (no parent)
  const rootSteps = steps.filter((s) => !s.parentStepId);

  // Build tree nodes for each root
  const rootTasks = rootSteps.map((step) =>
    buildTreeNode(step, childrenByParent)
  );

  // Compute aggregates for each root task
  const aggregatesById = new Map<string, TaskAggregates>();
  for (const node of rootTasks) {
    aggregatesById.set(node.step.id, computeAggregates(node, stepsById));
  }

  return {
    rootTasks,
    childrenByParent,
    aggregatesById,
  };
}

/**
 * Get overall plan progress from all steps
 */
export function getPlanProgress(steps: PlanStep[]): PlanProgress {
  const total = steps.length;
  const done = steps.filter((s) => s.status === StepStatus.Completed).length;
  const inProgress = steps.filter(
    (s) => s.status === StepStatus.InProgress
  ).length;
  const blocked = steps.filter((s) => s.status === StepStatus.Blocked).length;
  const percentComplete = total > 0 ? (done / total) * 100 : 0;

  return {
    total,
    done,
    inProgress,
    blocked,
    percentComplete,
  };
}

/**
 * Get root tasks grouped by phase
 */
export function getRootTasksByPhase(
  tree: TaskTree,
  phaseIds: string[]
): Map<string, TaskTreeNode[]> {
  const byPhase = new Map<string, TaskTreeNode[]>();

  // Initialize with empty arrays for all phases
  for (const phaseId of phaseIds) {
    byPhase.set(phaseId, []);
  }

  // Also add an 'unassigned' bucket
  byPhase.set('unassigned', []);

  // Group root tasks by their phase
  for (const node of tree.rootTasks) {
    const phaseId = node.step.phaseId || 'unassigned';
    const bucket = byPhase.get(phaseId) || [];
    bucket.push(node);
    byPhase.set(phaseId, bucket);
  }

  return byPhase;
}

/**
 * Find the global "next up" task across all root tasks
 */
export function findGlobalNextUp(tree: TaskTree): PlanStep | null {
  for (const node of tree.rootTasks) {
    const aggregates = tree.aggregatesById.get(node.step.id);
    if (aggregates?.nextUp) {
      return aggregates.nextUp;
    }
  }
  return null;
}

/**
 * Get all blocked steps with their blocking dependencies
 */
export interface BlockedStepInfo {
  step: PlanStep;
  blockedBy: PlanStep[];
}

export function getBlockedSteps(
  steps: PlanStep[]
): BlockedStepInfo[] {
  const stepsById = new Map(steps.map((s) => [s.id, s]));
  const blockedSteps: BlockedStepInfo[] = [];

  for (const step of steps) {
    if (step.status === StepStatus.Blocked) {
      const blockedBy = (step.dependsOn || [])
        .map((id) => stepsById.get(id))
        .filter((s): s is PlanStep => s !== undefined && s.status !== StepStatus.Completed);
      
      blockedSteps.push({ step, blockedBy });
    }
  }

  return blockedSteps;
}

/**
 * Get recent status updates across all steps, sorted by date
 */
export interface RecentUpdate {
  stepId: string;
  stepTitle: string;
  status: StepStatus;
  changedAt: string;
  reason?: string | null;
}

export function getRecentUpdates(
  steps: PlanStep[],
  limit: number = 10
): RecentUpdate[] {
  const updates: RecentUpdate[] = [];

  for (const step of steps) {
    if (step.statusHistory) {
      for (const event of step.statusHistory) {
        updates.push({
          stepId: step.id,
          stepTitle: step.title,
          status: event.status,
          changedAt: event.changedAt,
          reason: event.reason,
        });
      }
    }
  }

  // Sort by date descending and limit
  return updates
    .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime())
    .slice(0, limit);
}

